import { useEffect, lazy, Suspense, useState, useContext } from "react";
import GroupsList from "./Components/GroupsList";
import { useGroupsViewInitialization } from "./Groups.hook";
import INFRAMARK_CONSTANTS from "common/Constants/inframark-constants";
import { groupsCSS } from "./Groups.style";
import { clearCustomerContextHeaders } from "common/HttpClient/CustomerContextHeaders";
import UserContext from "common/Context/UserContext";
import GROUPS_CONSTANTS from "./GroupsConstant";
import {
  fetchMeCustomerAccessRows,
  syncProfileCustomerAccessSession,
} from "common/Auth/InitializeUserSessionFromMe";

const Loading = lazy(() => import("common/Components/Loading"));
const AddEditGroup = lazy(() => import("./Components/AddEditGroup"));
const NoDataFound = lazy(() => import("common/Components/NoDataFound"));
const Toast = lazy(() => import("common/Components/Toast"));

const Groups = () => {
  const { state, actions } = useGroupsViewInitialization();
  const { setCustomersList, userProfile, loadUserInformation } =
    useContext(UserContext);
  const [toast, setToast] = useState({
    show: false,
    type: GROUPS_CONSTANTS.toast.TYPE_FAILURE,
    title: "",
    description: "",
  });
  const Constants = INFRAMARK_CONSTANTS.groups.constants;

  useEffect(() => {
    const styleId = "groups-component-styles";
    let styleElement = document.getElementById(styleId);

    if (!styleElement) {
      styleElement = document.createElement("style");
      styleElement.id = styleId;
      styleElement.textContent = groupsCSS;
      document.head.appendChild(styleElement);
    }
  }, []);

  useEffect(() => {
    if (state.error) {
      setToast({
        show: true,
        type: GROUPS_CONSTANTS.toast.TYPE_FAILURE,
        title: GROUPS_CONSTANTS.groups.TOAST_ERROR_TITLE,
        description: state.error,
      });
    }
  }, [state.error]);

  const handleToastClose = () => {
    setToast((prev) => ({ ...prev, show: false }));
    if (state.error && state.selectedView !== Constants.GROUPLISTVIEW) {
      actions.refreshError();
    }
  };
  useEffect(() => {
    const selectedCustomer = localStorage.getItem("selected_customer");
    if (selectedCustomer) {
      localStorage.removeItem("selected_customer");
    }
    clearCustomerContextHeaders();
    if (state.groupsList === null) {
      actions.getAllGroups();
    }
  }, []);
  if (state.loading && state.groupsList == null) {
    return (
      <Suspense fallback={<div>{GROUPS_CONSTANTS.loading.FALLBACK}</div>}>
        <Loading />
      </Suspense>
    );
  }
  return (
    <Suspense fallback={<div>{GROUPS_CONSTANTS.loading.FALLBACK}</div>}>
      <>
        <Toast
          type={toast.type}
          title={toast.title}
          description={toast.description}
          show={toast.show}
          onClose={handleToastClose}
        />
        {state.selectedView === Constants.GROUPLISTVIEW ? (
              <>
                {state.error && (!state.groupsList || state.groupsList.length === 0) ? (
                  <Suspense fallback={<div>{GROUPS_CONSTANTS.loading.FALLBACK}</div>}>
                    <NoDataFound 
                      message={GROUPS_CONSTANTS.groups.NO_CUSTOMERS_FOUND} 
                      onRefresh={() => actions.getAllGroups()}
                    />
                  </Suspense>
                ) : (
                  <GroupsList
                    groups={state.groupsList || []}
                    handleCreateGroup={() => {
                      actions.changeViewStatus(Constants.GROUPCREATEVIEW, null);
                    }}
                    handelEditGroups={(group) => {
                      actions.changeViewStatus(Constants.GROUPEDITEVIEW, group);
                    }}
                    handleEditGroupAction={(groupInfo, code) => {
                      actions.patchGroupInfo(
                        groupInfo,
                        code,
                        Constants.GROUPLISTVIEW,
                        async () => {
                          const rows = await fetchMeCustomerAccessRows();
                          if (rows) {
                            setCustomersList?.(rows);
                            await syncProfileCustomerAccessSession(
                              userProfile?.profile,
                              rows,
                              loadUserInformation
                            );
                          }
                          actions.getAllGroups();
                        }
                      );
                    }}
                    handelPreviewGroups={(group) => {
                      actions.changeViewStatus(Constants.GROUPPREVIEW, group);
                    }}
                    handleSearchGroup={(searchText) => {
                      actions.searchGroups(searchText);
                    }}
                    getAllGroups={actions.getAllGroups}
                  />
                )}
              </>
            ) : (
              <Suspense fallback={<div>{GROUPS_CONSTANTS.loading.FALLBACK}</div>}>
                <AddEditGroup
                  selectedGroup={state.selectedGroup}
                  selectedView={state.selectedView}
                  allGroups={state.groups}
                  handleAddGroupAction={(groupInfo) => {
                    if (state.selectedView === Constants.GROUPCREATEVIEW) {
                      actions.addNewGroup(
                        groupInfo,
                        Constants.GROUPLISTVIEW,
                        () => {}
                      );
                    }
                  }}
                  handleCancel={() => {
                    actions.changeViewStatus(Constants.GROUPLISTVIEW, null);
                  }}
                />
              </Suspense>
            )}
      </>
    </Suspense>
  );
};
export default Groups;
